import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { AlertCircle, Calendar, GitMerge, XClose } from "@untitledui/icons";
import { useEffect, useState } from "react";
import type { Key } from "react-aria-components";
import z from "zod";
import { IconButton } from "@/components/Atoms/IconButton/IconButton";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tab, TabList, Tabs } from "@/components/application/tabs/tabs";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { DrawerHeader } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { Illustration } from "@/components/shared-assets/illustrations";
import { SubContractorDeleteControl } from "@/features/sub-contractors/SubContractorDeleteControl";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { fieldInfoQueryOptions } from "@/services/field-info";
import { organizationQueryOptions } from "@/services/organization";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export const ModeSchema = z.enum(["edit", "view"]);
export const TabSchema = z.enum(["details", "sub-organizations", "activity"]);

export const SearchSchema = z.object({
	mode: ModeSchema.optional().default("view"),
	tab: TabSchema.optional(),
	organizationId: z.string().optional(),
	subContractorId: z.string().optional(),
});

export type Mode = z.infer<typeof ModeSchema>;
export type Tab = z.infer<typeof TabSchema>;
export type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId",
)({
	component: RouteComponent,
	params: {
		parse: (params) => ({
			subConId: z.string().parse(params.subConId),
		}),
	},
	validateSearch: (search) => {
		const raw =
			search && typeof search === "object"
				? { ...(search as Record<string, unknown>) }
				: {};
		if (raw.tab === "users") {
			raw.tab = "details";
		}
		return SearchSchema.parse(raw);
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-contractors/(sub-con)/$subConId",
	});
	const [isOpen, setIsOpen] = useState(false);

	const currentTab = (location.pathname.split("/").pop() as Tab) || "details";
	const canUpdateSubContractor = useEntityActionAllowed({
		name: "UpdateSubContractor",
		entityType: "SubContractor",
		entityId: params.subConId,
		enabled: params.subConId.length > 0,
	});
	const canUpdateSubOrganization = useEntityActionAllowed({
		name: "UpdateSubOrganization",
		entityType: "SubOrganization",
		enabled: params.subConId.length > 0,
	});
	const canEditAssignments =
		canUpdateSubContractor.allowed && canUpdateSubOrganization.allowed;
	const assignmentEditPending =
		canUpdateSubContractor.isPending || canUpdateSubOrganization.isPending;

	const subContractorByIdQuery = useQuery(
		organizationQueryOptions.subContractors.detail(
			params.orgId,
			params.subConId,
		),
	);
	const subContractorById = subContractorByIdQuery.data;
	const labelQuery = useQuery(
		fieldInfoQueryOptions.label("SubContractor", {
			organizationId: params.orgId,
		}),
	);
	const subContractorLabel =
		labelQuery.data?.trim() || t("subContractor.entityLabel");
	const subContractorName = subContractorById?.name ?? subContractorLabel;

	useEffect(() => {
		setIsOpen(true);
	}, []);

	useEffect(() => {
		if (
			search.mode !== "edit" ||
			currentTab !== "sub-organizations" ||
			assignmentEditPending ||
			canEditAssignments
		) {
			return;
		}
		navigate({
			to: "/org-admin/$orgId/sub-contractors/$subConId/sub-organizations",
			params: { orgId: params.orgId, subConId: params.subConId },
			search: { mode: "view" },
			replace: true,
		});
	}, [
		assignmentEditPending,
		canEditAssignments,
		currentTab,
		navigate,
		params.orgId,
		params.subConId,
		search.mode,
	]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: "/org-admin/$orgId/sub-contractors",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const assignmentEditLocked =
		currentTab === "sub-organizations" &&
		search.mode === "edit" &&
		canEditAssignments &&
		!assignmentEditPending;

	const handleOpenChange = (open: boolean) => {
		if (!open && assignmentEditLocked) {
			return;
		}
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = subContractorById?.name
		? subContractorById.name
		: t("org-admin.sub-contractors.drawer.title");
	const tabs = [
		{
			label: t("org-admin.sub-contractors.tabs.details"),
			id: "details",
		},
		{
			label: `${t("subOrganization.entityLabel")}s`,
			id: "sub-organizations",
		},
		{ label: t("org-admin.sub-contractors.tabs.activity"), id: "activity" },
	];
	const metadata = [
		...(subContractorById?.created
			? [
					{
						id: "created",
						label: createdMetadataLabel(t, subContractorById.created),
						icon: Calendar,
					},
				]
			: []),
		...(subContractorById?.lastUpdated
			? [
					{
						id: "updated",
						label: updatedMetadataLabel(t, subContractorById.lastUpdated),
						icon: Calendar,
					},
				]
			: []),
	];

	const goToTab = (tabId: string) => {
		if (assignmentEditLocked) return;
		navigate({
			to: `/org-admin/$orgId/sub-contractors/$subConId/${tabId}`,
			params: {
				orgId: params.orgId,
				subConId: params.subConId,
			},
			search: { mode: "view" },
		});
	};

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			width="w-[570px]"
			backdropPreset="transparent"
			dialogClassName="gap-0 overflow-hidden"
		>
			{({ close }) => (
				<div className="flex h-full min-h-0 w-full flex-col">
					<DrawerHeader
						type="default"
						title={title}
						featuredIcon={GitMerge}
						showSpacer={false}
						metadata={metadata}
						overflowControl={
							<>
								<SubContractorDeleteControl
									organizationId={params.orgId}
									subContractorId={params.subConId}
									name={subContractorName}
									label={subContractorLabel}
									onDeleted={() => {
										close();
										handleClose();
									}}
								/>
								<IconButton
									aria-label={t("common.actions.close")}
									hierarchy="secondary"
									size="sm"
									shape="square"
									icon={XClose}
									isDisabled={assignmentEditLocked}
									onPress={() => {
										if (assignmentEditLocked) return;
										close();
										handleClose();
									}}
								/>
							</>
						}
					/>
					{subContractorByIdQuery.isLoading ? (
						<div className="flex min-h-0 flex-1 items-center justify-center bg-tertiary">
							<LoadingIndicator type="line-simple" size="xl" supportingText />
						</div>
					) : subContractorByIdQuery.isError ? (
						<div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 bg-tertiary">
							<Illustration type="cloud" size="sm">
								<div className="bg-error-500 rounded-full p-3">
									<AlertCircle className="size-6" />
								</div>
							</Illustration>
							<div className="flex flex-col items-center justify-center pt-4">
								<p className="text-gray-900 font-semibold text-md">
									{t(
										"org-admin.sub-contractors.table.errors.errorLoadingPage",
									)}
								</p>
								<div className="w-[60%]">
									<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
										{t(
											"org-admin.sub-contractors.table.errors.errorLoadingContent",
										)}
									</p>
								</div>
							</div>
						</div>
					) : (
						<Drawer.ScrollBody showPlaceholder={false}>
							<NativeSelect
								aria-label={t("org-admin.sub-contractors.tabs.details")}
								value={currentTab}
								onChange={(event) => goToTab(event.target.value)}
								options={tabs.map((tab) => ({
									label: tab.label,
									value: tab.id,
								}))}
								className="mx-6 mt-3 w-80 md:hidden"
							/>
							<div className="sticky top-0 z-10 flex h-12 w-full shrink-0 flex-col items-stretch border-b border-secondary bg-primary px-(--spacing-3xl,24px) max-md:hidden">
								<Tabs
									selectedKey={currentTab}
									onSelectionChange={(key: Key) => goToTab(String(key))}
									className="h-full w-full"
								>
									<TabList
										type="underline"
										size="sm"
										items={tabs}
										className="h-full w-full items-stretch gap-(--spacing-lg,12px) border-b-0"
									>
										{(item) => (
											<Tab
												id={item.id}
												label={item.label}
												className="h-full items-end -mb-px"
											/>
										)}
									</TabList>
								</Tabs>
							</div>
							<div className="flex w-full flex-col gap-(--spacing-xs,4px) p-(--spacing-xl,16px)">
								<TabContentWrapper tabKey={currentTab}>
									<Outlet />
								</TabContentWrapper>
							</div>
						</Drawer.ScrollBody>
					)}
				</div>
			)}
		</Drawer>
	);
}
